import 'dayjs/locale/de';
import 'dayjs/locale/fr';
// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs';
// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// eslint-disable-next-line no-restricted-imports
export type { Dayjs } from 'dayjs';

dayjs.locale('de');

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
// Enables `.format('LL')` etc. (locale-aware long date formats) — used by
// callers that render dates in a locale other than the German default, e.g.
// the Reviews widget's de/fr publish-date display.
dayjs.extend(localizedFormat);
// Required for the `dayjs(input, format, true)` strict-parsing form used by
// `parseGermanDate` and by callers validating date-only input. Without it dayjs
// ignores the format and strict flag entirely and falls back to `new Date()`.
dayjs.extend(customParseFormat);

const DEFAULT_TIMEZONE = 'Europe/Berlin';
dayjs.tz.setDefault(process.env.TZ ?? DEFAULT_TIMEZONE);

/**
 * This patched version of dayjs will format times in Europe/Berlin timezone.
 *
 * #Examples
 *
 * ##Formatting
 * ```
 * import dayjs from '@shared/dayjs';
 * dayjs(<input>).format();
 * ```
 *
 * ##Formatting in a different timezone
 * ```
 * import dayjs from '@shared/dayjs';
 * dayjs(<input>).tz('America/New_York').format();
 * ```
 *
 * ##Parsing in Berlin-local time
 * `dayjs.tz(<input>, 'Europe/Berlin')`
 *
 * ##Parsing properly formatted input with timestamp
 * `dayjs(<input>)`
 */
const dayjsWithDefaultTimezone = ((
  ...args: Parameters<typeof dayjs>
): ReturnType<typeof dayjs> => {
  return dayjs(...args).tz(DEFAULT_TIMEZONE);
}) as typeof dayjs;
Object.assign(dayjsWithDefaultTimezone, dayjs);

export default dayjsWithDefaultTimezone;

/**
 * A calendar date without a time or a zone — the shape a Strapi `date` column
 * stores, and the format `<input type="date">` reads and writes.
 */
export const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD'; // 2001-01-01
export const DEFAULT_GERMAN_DATE_FORMAT_DAY_MONTH = 'DD.MM.'; // 01.01.
export const DEFAULT_GERMAN_DATE_FORMAT = 'DD.MM.YYYY'; // 01.01.2001
export const DEFAULT_GERMAN_DATE_TIME_FORMAT = `${DEFAULT_GERMAN_DATE_FORMAT} HH:mm [Uhr]`; // 01.01.2001 12:34 Uhr
export const DEFAULT_GERMAN_DATE_TIME_FORMAT_SHORT = `${DEFAULT_GERMAN_DATE_FORMAT_DAY_MONTH} HH:mm`; // 01.01. 12:34
const GERMAN_DATE_FORMATS = [
  'D.M.YYYY', // 1.1.2001
  DEFAULT_GERMAN_DATE_FORMAT,
  'D.MM.YYYY', // 1.01.2001 (inconsistent but still considered valid)
  'DD.M.YYYY', // 01.1.2001 (inconsistent but still considered valid)
];
export const DEFAULT_GERMAN_TZ = DEFAULT_TIMEZONE;
export const parseGermanDate = (input: string): ReturnType<typeof dayjs> => {
  return dayjs(input, GERMAN_DATE_FORMATS, true).tz(DEFAULT_TIMEZONE, true);
};

export const formatDisplayDate = (isoDate: string, locale = 'de'): string =>
  dayjs(isoDate).locale(locale).format(DEFAULT_GERMAN_DATE_FORMAT_DAY_MONTH);

export const formatFullDate = (isoDate: string, locale = 'de'): string =>
  dayjs(isoDate).locale(locale).format(DEFAULT_GERMAN_DATE_FORMAT);

export const formatWeekday = (isoDate: string, locale = 'de'): string =>
  dayjs(`${isoDate}T12:00:00`).locale(locale).format('ddd');

export const formatToGermanDate = (
  input?: string | Dayjs | null,
  format = DEFAULT_GERMAN_DATE_FORMAT,
  fallback = '-',
): string => {
  if (!input) {
    return fallback;
  }
  let output = dayjs(input).tz(DEFAULT_TIMEZONE, true).format(format);
  if (output === 'Invalid Date') {
    output = fallback;
  }
  return output;
};
